import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import { PAGES } from '../../constants/pages';
import { useUpdateLocationsWithTargetStockMutation, TotadminPermission } from 'data/__generated__';

import { Button } from '@/components/ui/button';
import { PageActions, PageBreadcrumb, PageBreadcrumbSegment, PageHeader } from 'components/PageHeader';
import { CsvDataRow, TotemCsvInputButton } from 'components/TotemCsvInputButton';
import { getLocationIdsWithTargetStockFromData } from './Imports/locationTargetStockImport';
import { LaunchRestockingFreshPopup } from './Popups/LaunchRestockingFreshPopup';
import { StockTransferGenerationPopup } from './Popups/StockTransferGenerationPopup';
import { QuantityGenerationPopup, type QuantityGenerationColumn } from './Popups/QuantityGenerationPopup';
import { SiteList } from './components/SiteList';
import { PermissionGate } from 'components/PermissionGate';

export const MicrostoreColumnGroups = () => {
    const [updateLocationsWithTargetStock] = useUpdateLocationsWithTargetStockMutation();

    const [selectedColumns, setSelectedColumns] = useState<QuantityGenerationColumn[]>([]);
    const [isLaunchRestockingFreshPopupOpen, setLaunchRestockingFreshPopupOpen] = useState(false);
    const [isStockTransferGenerationPopupOpen, setStockTransferGenerationPopupOpen] = useState(false);
    const [isQuantityGenerationPopupOpen, setQuantityGenerationPopupOpen] = useState(false);

    async function handleCSVImport(csvData: CsvDataRow[]) {
        const locationIdsWithTargetStock = getLocationIdsWithTargetStockFromData(csvData);

        if (!locationIdsWithTargetStock.length) {
            return;
        }

        await updateLocationsWithTargetStock({ variables: { locationIdsWithTargetStock } });

        toast.success('Mise à jour réussie');
    }

    return (
        <Container>
            <Helmet title="Groupes de colonnes microstore · TOTADMIN" />
            <PageHeader>
                <PageBreadcrumb>
                    <PageBreadcrumbSegment label="Groupes microstore" isCurrent />
                </PageBreadcrumb>
                <PageActions>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setLaunchRestockingFreshPopupOpen(true)}
                        requiredPermissions={[TotadminPermission.MicrostoreColumnsManage]}
                    >
                        Restocking
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setStockTransferGenerationPopupOpen(true)}
                        requiredPermissions={[TotadminPermission.MicrostoreColumnsManage]}
                    >
                        Transferts de stock{selectedColumns.length ? ` (${selectedColumns.length})` : ''}
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setQuantityGenerationPopupOpen(true)}
                        requiredPermissions={[TotadminPermission.MicrostoreColumnsManage]}
                    >
                        Quantités{selectedColumns.length ? ` (${selectedColumns.length})` : ''}
                    </Button>
                    <Button size="sm" variant="outline" asChild>
                        <Link
                            to={PAGES.microstoreColumnGroupTargetsInfo.url}
                            title="Afficher les infos de remplissage des stores post restocking"
                        >
                            Synthèse
                        </Link>
                    </Button>
                    {/* `TotemCsvInputButton` n'a pas de verrou : l'import écrit les stocks cibles. */}
                    <PermissionGate requiredPermissions={[TotadminPermission.MicrostoreColumnsManage]}>
                        <TotemCsvInputButton onCsvDataUpload={handleCSVImport} label="Importer" />
                    </PermissionGate>
                </PageActions>
            </PageHeader>
            <Content>
                {/* Liste issue des sites (`sites_read`) : sans ce droit, elle ferait tomber la page. */}
                <PermissionGate requiredPermissions={[TotadminPermission.SitesRead]}>
                    <SiteList onSelectedColumnsChange={setSelectedColumns} />
                </PermissionGate>
            </Content>
            <LaunchRestockingFreshPopup
                isOpen={isLaunchRestockingFreshPopupOpen}
                setIsOpen={setLaunchRestockingFreshPopupOpen}
            />
            {isStockTransferGenerationPopupOpen ? (
                <StockTransferGenerationPopup
                    isOpen={isStockTransferGenerationPopupOpen}
                    setIsOpen={setStockTransferGenerationPopupOpen}
                    initialColumns={selectedColumns}
                    showOpenSitesShortcut
                />
            ) : null}
            {isQuantityGenerationPopupOpen ? (
                <QuantityGenerationPopup
                    isOpen={isQuantityGenerationPopupOpen}
                    setIsOpen={setQuantityGenerationPopupOpen}
                    initialColumns={selectedColumns}
                    showOpenSitesShortcut
                />
            ) : null}
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
`;
